import React, { useState, useEffect } from 'react';
import Layout from '@layout/index';
import { IconButton, Grid, TextField, Typography } from '@mui/material/';
import { SearchOutlined } from '@mui/icons-material';
import { logs } from '@cosmjs/stargate';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import { useStores } from '@stores';
import { BEARS } from '@api';
import Table from '@components/Table';
import {
  numberWithSeparator,
  floorRounding,
  findAttribute,
  splitCamelCase,
  findEvent,
} from '@utils';
import { MILLION_MULTIPLIER } from '@constants/system';
import { GOLDEN_BEE_ADDRESSES, GOLDEN_BEE_MONTH } from 'src/constants/goldenBeeClub';
import BeeSpinnerLoading from '@components/BeeSpinnerLoading';
import TestnetPolygon from '@assets/MainPage/testnet_polygon.svg';
import HoneyCoinsIndicatorIcon from '@assets/MainPage/honey_coins_indicator_icon.svg';
import ConeIndicatorIcon from '@assets/MainPage/cone_indicator_icon.svg';
import AirQualityIcon from '@assets/MainPage/air_quality_icon.svg';
import BlockHeightIcon from '@assets/MainPage/block_height_icon.svg';
import BearIcon from '@assets/MainPage/bear_icon.svg';
import PvpPveIcon from '@assets/MainPage/pvp_pve_icon.svg';
import RobotIcon from '@assets/MainPage/robot_icon.svg';
import BeeIcon from '@assets/MainPage/bee_icon.svg';
import ApiaryIcon from '@assets/MainPage/apiary_icon.svg';
import TreesIcon from '@assets/MainPage/trees_icon.svg';
// import ActiveValidatorsIcon from '@assets/MainPage/active_validators_icon.svg';
// import TsxIcon from '@assets/MainPage/tsx.svg';
import TotalPlayersIcon from '@assets/MainPage/total_players_icon.svg';
import { useNavigate } from 'react-router-dom';

import styles from './mainPage.module.scss';

const ParametersIndicator = ({
  indicatorIcon = HoneyCoinsIndicatorIcon,
  title = '',
  firstParameter = '',
  secondParameter = '',
  isStaking = false,
  stakedCones,
  unstakingCones,
  onIndicatorClick = () => {},
  isClickable = false,
}) => {
  return (
    <div
      className={classnames(
        styles.IndicatorContainer,
        isClickable && styles.IndicatorContainer__Clickable,
      )}
      type="button"
      role="button"
      tabIndex="0"
      onClick={() => {
        onIndicatorClick();
      }}
      onKeyPress={() => {
        onIndicatorClick();
      }}
    >
      <img src={indicatorIcon} alt="indicator_icon" className={styles.IndicatorIcon} />
      <p className={styles.IndicatorTitle}>{title}</p>
      <p className={styles.IndicatorParameters}>
        {firstParameter
          ? secondParameter
            ? `${numberWithSeparator(firstParameter)}/${numberWithSeparator(secondParameter)}`
            : `${numberWithSeparator(firstParameter)}`
          : '-'}
      </p>
      {isStaking && (
        <div className={styles.Staking}>
          <div className={styles.StakingTitleContainer}>
            <p className={styles.StakingTitleText}>In stake</p>
            <p className={styles.StakingTitleText}>Unstaking</p>
          </div>
          <div className={styles.StakingQuantityContainer}>
            <p className={styles.StakingQuantityText}>
              {stakedCones ? numberWithSeparator(stakedCones) : '-'}
            </p>
            <p className={styles.StakingQuantityText}>
              {unstakingCones ? numberWithSeparator(unstakingCones) : '-'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const GameInformationIndicator = ({
  indicatorIcon = HoneyCoinsIndicatorIcon,
  title = '',
  parameter,
  link = '',
  navigate,
}) => {
  const onIndicatorClick = () => {
    if (link) {
      navigate(link);
    }
  }

  return (
    <Grid
      container
      flexDirection={{ xs: 'column', md: 'row' }}
      alignItems="center"
      justifyContent={{ xs: 'center', md: 'flex-start' }}
    >
      <Grid item xs={12} md={4}>
        <img
          src={indicatorIcon}
          alt="indicator_icon"
          className={styles.GameInformationIndicatorIcon}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={8}
        container
        flexDirection="column"
        alignItems={{ xs: 'center', md: 'flex-start' }}
      >
        <div
          className={classnames(
            styles.GameInformationIndicatorContainer,
            link && styles.GameInformationIndicatorContainer__Clickable,
          )}
          type="button"
          role="button"
          tabIndex="0"
          onClick={() => {
            onIndicatorClick();
          }}
          onKeyPress={() => {
            onIndicatorClick();
          }}
        >
          <p className={styles.GameInformationIndicatorTitle}>{title}</p>
          <p className={styles.GameInformationParameters}>
            {parameter ? numberWithSeparator(parameter) : '-'}
          </p>
        </div>
      </Grid>
    </Grid>
  );
};

const MainPage = observer(() => {
  const navigate = useNavigate();
  const stores = useStores();
  const [address, setAddress] = useState('');
  const [parametersIndicatorData, setParametersIndicatorData] = useState([]);
  const [gameInfoData, setGameInfoData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isTransactionsTablesLoading, setTransactionsTablesLoading] = useState(true);
  const [isBlocksTablesLoading, setBlocksTablesLoading] = useState(true);
  const [isTransactionsTablePaginationLoading, setTransactionsTablePaginationLoading] =
    useState(false);
  const [isBlocksTablePaginationLoading, setBlocksTablePaginationLoading] = useState(false);
  const [blockHeight, setBlockHeight] = useState(null);

  const [latestTransactionsInfo, setLatestTransactionsInfo] = useState([]);
  /*   const [totalCountInfo, setTotalCountInfo] = useState(null); */
  const [latestTransactionsPage, setLatestTransactionsPage] = useState(0);

  const [latestBlocksInfo, setLatestBlocksInfo] = useState([]);
  const [latestBlocksPage, setLatestBlocksPage] = useState(0);

  useEffect(() => {
    if (stores.isAnonymousClientConfigured) {
      (async () => {
        try {
          const response = await fetch(`https://honeywood.io/api/match3/stats`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          // eslint-disable-next-line camelcase
          const { pve, pvp, total_players } = await response.json();

          const poolCones = await BEARS.getAllStakedAndUnstakingCones();
          const airInfo = await BEARS.getLastAirInfo();
          const totalHoneyInGame = await BEARS.getTotalSupplyHoney();
          const totalConesInGame = await BEARS.getTotalSupplyCones();
          // const activeValidators = await BEARS.getValidators('BOND_STATUS_BONDED');
          const blockHeight = await BEARS.getBlockHeights();
          const totalBears = await BEARS.getAllBears();
          const totalBees = await BEARS.getAllBees();
          const totalTrees = await BEARS.getAllTrees();
          const totalApiaries = await BEARS.getAllApiaries();
          setParametersIndicatorData([
            {
              title: 'Total HoneyCoins in game',
              indicatorIcon: HoneyCoinsIndicatorIcon,
              firstParameter: totalHoneyInGame,
            },
            {
              title: 'Total CONEs in game',
              indicatorIcon: ConeIndicatorIcon,
              firstParameter: totalConesInGame,
              isStaking: true,
              stakedCones: poolCones?.bondedTokens,
              unstakingCones: poolCones?.notBondedTokens,
            },
            {
              title: 'Air Quality',
              indicatorIcon: AirQualityIcon,
              firstParameter: floorRounding(airInfo?.count),
              secondParameter: airInfo?.purity,
              onIndicatorClick: () => {
                navigate(`/air-quality`);
              },
              isClickable: true,
            },
            {
              title: 'Block Height',
              indicatorIcon: BlockHeightIcon,
              firstParameter: blockHeight,
            },
            /*   {
              title: 'Active Validators',
              indicatorIcon: ActiveValidatorsIcon,
              firstParameter: activeValidators?.validators?.length,
            }, */
            /* // ! totalCount temporary not needed */
            /* {
              title: 'Transactions',
              indicatorIcon: TsxIcon,
              firstParameter: totalCountInfo || '-',
            }, */
            {
              title: 'Total Games',
              indicatorIcon: PvpPveIcon,
              firstParameter: floorRounding(Number(pve) + Number(pvp), 0) || '-',
            },
          ]);
          setGameInfoData([
            {
              title: 'Total bears',
              indicatorIcon: BearIcon,
              parameter: totalBears?.pagination?.total,
            },
            {
              title: 'Total players',
              indicatorIcon: TotalPlayersIcon,
              parameter: `${floorRounding(Number(total_players) * 0.001, 2)}K` || '-',
            },
            {
              title: 'Total bees',
              indicatorIcon: BeeIcon,
              parameter: totalBees?.pagination?.total,
            },
            {
              title: 'Total apiaries',
              indicatorIcon: ApiaryIcon,
              parameter: totalApiaries?.pagination?.total,
            },
            {
              title: 'Total trees',
              indicatorIcon: TreesIcon,
              parameter: totalTrees?.pagination?.total,
            },
            {
              title: 'Golden Bee',
              indicatorIcon: TotalPlayersIcon,
              parameter: GOLDEN_BEE_ADDRESSES[GOLDEN_BEE_MONTH].length,
              link: 'golden-bee',
            },
          ]);
          setBlockHeight(blockHeight);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured]);

  useEffect(() => {
    if (stores.isAnonymousClientConfigured && blockHeight) {
      (async () => {
        try {
          setBlocksTablePaginationLoading(true);
          const latestBlocks = await BEARS.getLatestBlocks(
            blockHeight - 9 - latestBlocksPage,
            blockHeight - latestBlocksPage,
          );
          if (latestBlocks?.blockMetas?.length) {
            setLatestBlocksInfo(
              latestBlocks.blockMetas.map((block) => {
                return [
                  [...Buffer.from(block?.blockId?.hash).toString('hex').slice(0, 30), '...'],
                  block?.blockSize,
                  block?.header?.height,
                  moment(block?.header?.time).format('DD.MM.YYYY, HH:mm:ss'),
                ];
              }),
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);
        } finally {
          setBlocksTablesLoading(false);
          setBlocksTablePaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, blockHeight, latestBlocksPage]);

  useEffect(() => {
    if (stores.isAnonymousClientConfigured && blockHeight) {
      (async () => {
        try {
          setTransactionsTablePaginationLoading(true);
          const result = await BEARS.getLatestTransactionsNew({ page: latestTransactionsPage });
          // const totalCountTxs = await BEARS.getTotalTransactions(latestTransactionsPage);
          // console.log('totalCountTxs: ', totalCountTxs);
          // console.log('totalCount: ', totalCount);
          /* setTotalCountInfo(totalCount); */

          /* if (totalCount) {
            const txIndex = parametersIndicatorData.findIndex(
              (item) => item.title === 'Transactions',
            );
            if (txIndex >= 0) {
              setParametersIndicatorData((parametersIndicatorData) => [
                ...parametersIndicatorData.slice(0, txIndex),
                Object.assign(parametersIndicatorData[txIndex], {
                  firstParameter: totalCount,
                }),
                ...parametersIndicatorData.slice(txIndex + 1),
              ]);
            }
          } */
          setLatestTransactionsInfo(result.txsInfo);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);
        } finally {
          setTransactionsTablesLoading(false);
          setTransactionsTablePaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, blockHeight, latestTransactionsPage]);

  const SearchClick = () => {
    (async () => {
      try {
        const bearName = await BEARS.getBearName(address);
        const bearsInfo = await BEARS.getBearsInfo({
          ids: [bearName?.bearId],
        });
        if (bearsInfo?.length && bearsInfo[0].owner) {
          setAddress(bearsInfo[0].owner);
          navigate(`/address/${bearsInfo[0].owner}`, {
            state: { address: bearsInfo[0].owner },
          });
        } else {
          navigate(`/address/${address}`, { state: { address } });
        }
      } catch (error) {
        navigate(`/address/${address}`, { state: { address } });
      }
    })();
  };

  const onKeyHandle = (event) => {
    if (event.charCode === 13 && event.target.value !== '') {
      SearchClick();
    }
  };
  const onChangeAddress = (event) => {
    setAddress(event.target.value.toLowerCase());
  };

  return (
    <Layout>
      <Grid
        container
        sx={{ maxWidth: '1660px', width: '100%' }}
        p={{ xs: '28px 30px 0', sm: '20px 60px 0', lg: '20px 100px 0', xl: '20px 130px 0' }}
      >
        <div className={styles.TestnetBlock}>
          <Grid
            container
            direction="column"
            sx={{ maxWidth: '1660px', width: '100%' }}
            m={{ xs: '20px', sm: '30px', md: '70px 140px 70px 40px' }}
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            className={styles.LeftInfoBlock}
          >
            <p className={styles.LeftInfoBlockTitle}>HoneyWood mainnet</p>
            <Grid
              container
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              className={styles.SubtitleBlock}
            >
              <Typography
                variant="p"
                component="p"
                sx={{ margin: { xs: '0 0 10px 0', sm: '0 60px 0 0' } }}
                className={styles.Subtitle}
              >
                Explorer
              </Typography>
              <div className={styles.TestnetSpan}>
                <p className={styles.TestnetText}>mainnet</p>
                <img
                  src={TestnetPolygon}
                  alt="testnet_polygon"
                  className={classnames(
                    styles.TestnetPolygon,
                    stores.browserInfo.isAndroid && styles.TestnetPolygon__isAndroid,
                  )}
                />
              </div>
            </Grid>
            <TextField
              id="textField-mainPage"
              variant="outlined"
              placeholder="Search Address / Nickname ..."
              onChange={onChangeAddress}
              onKeyPress={onKeyHandle}
              className={styles.TextField}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={SearchClick} size="large">
                    <SearchOutlined />
                  </IconButton>
                ),
              }}
              sx={{
                width: '100%',
                maxWidth: { xs: '350px', sm: '400px', md: '500px' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiButtonBase-root': {
                  color: '#F2A71E',
                },
              }}
            />
          </Grid>
          <div className={styles.RightInfoBlock} />
        </div>
        <div className={styles.ParametersContainer}>
          {isLoading ? (
            <BeeSpinnerLoading classNames={{ Spinner: styles.LoadingParametersContainer }} />
          ) : (
            parametersIndicatorData.length > 0 &&
            parametersIndicatorData.map((item) => (
              <ParametersIndicator
                title={item.title}
                indicatorIcon={item.indicatorIcon}
                firstParameter={item.firstParameter}
                secondParameter={item.secondParameter}
                key={item.title}
                isStaking={item.isStaking}
                stakedCones={item.stakedCones}
                unstakingCones={item.unstakingCones}
                onIndicatorClick={item.onIndicatorClick}
                isClickable={item.isClickable}
              />
            ))
          )}
        </div>
        <div className={styles.GameInformationContainer}>
          <Typography
            variant="p"
            component="p"
            textAlign={{ xs: 'center', md: 'left' }}
            mb="20px"
            className={styles.GameInformationTitle}
          >
            Game information
          </Typography>
          {isLoading ? (
            <BeeSpinnerLoading classNames={{ Spinner: styles.LoadingGameInformationContainer }} />
          ) : (
            <Grid
              container
              justifyContent={{ xs: 'center', md: 'space-between' }}
              spacing={{ xs: 3, sm: 3, md: 1 }}
            >
              {gameInfoData.length > 0 &&
                gameInfoData.map((item) => (
                  <Grid item width="175px" key={item.title}>
                    <GameInformationIndicator
                      title={item.title}
                      indicatorIcon={item.indicatorIcon}
                      parameter={item.parameter}
                      link={item.link}
                      navigate={navigate}
                    />
                  </Grid>
                ))}
            </Grid>
          )}
        </div>
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4, lg: 6 }}
          sx={{ width: '100%' }}
          mb={{ xs: '20px', sm: '30px', lg: '50px' }}
        >
          <Grid item xs={12} lg={6}>
            {isTransactionsTablesLoading ? (
              <BeeSpinnerLoading
                classNames={{
                  Container: styles.LoadingTablesContainer,
                  Spinner: styles.LoadingTablesSpinner,
                }}
              />
            ) : latestTransactionsInfo?.length ? (
              <Table
                title="Latest Transactions"
                header={['', 'Transaction', 'Block', 'Value', 'Action']}
                rows={latestTransactionsInfo}
                isActiveTable
                customCellWhiteSpace="pre"
                isSpinning={isTransactionsTablePaginationLoading}
                isHugeTotalCount
                onClickPrevButton={() => {
                  setLatestTransactionsPage(
                    latestTransactionsPage ? latestTransactionsPage - 1 : latestTransactionsPage,
                  );
                }}
                onClickNextButton={() => {
                  setLatestTransactionsPage(latestTransactionsPage + 1);
                }}
                disabledNextButton={isTransactionsTablePaginationLoading}
                disabledPrevButton={!latestTransactionsPage || isTransactionsTablePaginationLoading}
                isCollapsibleTable
              />
            ) : (
              <p className={styles.EmptyTableTitle}>No transactions</p>
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            {isBlocksTablesLoading ? (
              <BeeSpinnerLoading
                classNames={{
                  Container: styles.LoadingTablesContainer,
                  Spinner: styles.LoadingTablesSpinner,
                }}
              />
            ) : latestBlocksInfo?.length > 0 ? (
              <Table
                title="Latest Blocks"
                header={['Hash', 'Size', 'Height', 'Time']}
                rows={latestBlocksInfo}
                isActiveTable
                isSpinning={isBlocksTablePaginationLoading}
                isHugeTotalCount
                onClickPrevButton={() => {
                  setLatestBlocksPage(latestBlocksPage ? latestBlocksPage - 10 : latestBlocksPage);
                }}
                onClickNextButton={() => {
                  setLatestBlocksPage(latestBlocksPage + 10);
                }}
                disabledPrevButton={!latestBlocksPage || isBlocksTablePaginationLoading}
                disabledNextButton={isBlocksTablePaginationLoading}
              />
            ) : (
              <p className={styles.EmptyTableTitle}>No blocks</p>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
});

export default MainPage;
